// ./src/utils/api/GuestContributionService.ts
//
// Guest budget contributions — what a guest pledged (intent) or reported already paying,
// from the event website's budget block. A BUDGET VIEW over the unified `event_guest_signup`
// table (which also backs signup-list / POI / accommodation / travel sign-ups): this service
// always scopes to targetKind='budget' and maps the budget-item pointer + off-list name +
// amount to/from the table's generic columns (target_id / target_name / amount / details), so
// callers never see the polymorphic columns. Read-mostly on the admin side — the host reviews
// contributions and confirms a `paid` one into a real ledger payment via BudgetPaymentService.
// Mirrors GuestStayService / PoiSignupService. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// This service always operates on budget targets in the unified table.
const BUDGET_TARGET_KIND = "budget";

// =============================================================================
// GraphQL
// =============================================================================

// Alias the unified table's polymorphic target back to budget-shaped fields. `details` carries
// the contribution kind (pledge | paid), the new-item categoryId, and — once the host confirms
// a paid contribution — confirmedPaymentId / confirmedAt.
const CONTRIBUTION_FIELDS = `
  id
  eventId
  accountId
  eventGuestId
  budgetItemId: targetId
  name: targetName
  amount
  note
  details
  status
  rights
  createdAt
  updatedAt
`;

const CONTRIBUTION_LIST_QUERY = gql`
  query EventGuestSignupList($filter: EventGuestSignupFilter, $params: PaginationAndSorting) {
    eventGuestSignupList(filter: $filter, params: $params) {
      code
      data { ${CONTRIBUTION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CONTRIBUTION_REMOVE_MUTATION = gql`
  mutation EventGuestSignupRemove($where: EventGuestSignupWhereInput) {
    eventGuestSignupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (the budget view; budgetItemId → target_id, kind lives in details)
// =============================================================================

export interface GuestContribution {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  eventGuestId?: string | null;
  /** The budget item this is toward; null for an off-list (suggested) item named by the guest. */
  budgetItemId?: string | null;
  /** Off-list item name. */
  name?: string | null;
  amount?: number | null;
  note?: string | null;
  /** { kind: 'pledge' | 'paid', categoryId?, confirmedPaymentId?, confirmedAt? }. */
  details?: Record<string, unknown> | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface GuestContributionFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  eventGuestId?: string[] | null;
  budgetItemId?: string[] | null;
  status?: number[] | null;
}

// Map the budget-scoped filter onto the unified table's generic filter:
// budget scope + budgetItemId → targetId.
const toApiFilter = (filter?: GuestContributionFilter): Record<string, unknown> => {
  const { budgetItemId, ...rest } = filter || {};
  return {
    ...rest,
    targetKind: [BUDGET_TARGET_KIND],
    ...(budgetItemId && budgetItemId.length > 0 ? { targetId: budgetItemId } : {}),
  };
};

// Convenience accessors for the kind + confirmation stamped in `details`.
export const contributionKind = (c: GuestContribution): "pledge" | "paid" =>
  (c?.details as { kind?: string } | null)?.kind === "paid" ? "paid" : "pledge";

export const isContributionConfirmed = (c: GuestContribution): boolean =>
  !!(c?.details as { confirmedPaymentId?: string } | null)?.confirmedPaymentId;

// =============================================================================
// Service
// =============================================================================

export class GuestContributionService {
  static async list(filter?: GuestContributionFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(CONTRIBUTION_LIST_QUERY, {
      filter: toApiFilter(filter),
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(CONTRIBUTION_REMOVE_MUTATION, { where: { id } });
  }
}
